import {
  FlipProp,
  PullProp,
  RotateProp,
  SizeProp,
} from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';
import { useMemo } from 'react';

const IconName = {
  faLightEnvelopeUser: 'light-envelope-user',
} as const;

export type IconNameType = (typeof IconName)[keyof typeof IconName];

const SIZES = [
  '2xs',
  'xs',
  'sm',
  'lg',
  'xl',
  '2xl',
  '1x',
  '2x',
  '3x',
  '4x',
  '5x',
  '6x',
  '7x',
  '8x',
  '9x',
  '10x',
];

interface FontAwesomeCustomIconProps extends React.HTMLAttributes<HTMLElement> {
  icon: IconNameType;
  // The font-size of the icon. Default is '1em'.
  // also provides SizeProp from FontAwesome
  size?: string | number | SizeProp;
  className?: string;
  color?: string;
  spin?: boolean;
  spinPulse?: boolean;
  spinReverse?: boolean;
  pulse?: boolean;
  beat?: boolean;
  fade?: boolean;
  beatFade?: boolean;
  bounce?: boolean;
  shake?: boolean;
  border?: boolean;
  fixedWidth?: boolean;
  inverse?: boolean;
  flip?: FlipProp;
  pull?: PullProp;
  rotation?: RotateProp;
  tabIndex?: number;
  title?: string;
  swapOpacity?: boolean;
}

export const FontAwesomeCustomIcon = (
  props: FontAwesomeCustomIconProps
): JSX.Element => {
  //#region ------------------------------ Defaults
  const {
    className,
    icon,
    size = '1em',
    color,
    style,
    spin,
    spinPulse,
    spinReverse,
    pulse,
    beat,
    fade,
    beatFade,
    bounce,
    shake,
    border,
    fixedWidth,
    inverse,
    flip,
    pull,
    rotation,
    swapOpacity,
    ...rest
  } = props;
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const fontSize = useMemo(() => {
    if (typeof size === 'number') {
      return `${size}px`;
    } else if (typeof size === 'string' && SIZES.includes(size as SizeProp)) {
      return undefined;
    } else if (typeof size === 'string') {
      return size;
    }
    return undefined;
  }, [size]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  if (!icon) {
    return null;
  }

  return (
    <i
      className={classNames(
        'fa-kit',
        {
          [`fa-${icon}`]: icon,
          [`fa-${size}`]: !fontSize,
          'fa-spin': spin,
          'fa-spin-pulse': spinPulse,
          'fa-spin-reverse': spinReverse,
          'fa-pulse': pulse,
          'fa-beat': beat,
          'fa-fade': fade,
          'fa-beat-fade': beatFade,
          'fa-bounce': bounce,
          'fa-shake': shake,
          'fa-border': border,
          'fa-fw': fixedWidth,
          'fa-inverse': inverse,
          [`fa-flip-${flip}`]: flip,
          [`fa-pull-${pull}`]: pull,
          [`fa-rotate-${rotation}`]: rotation,
          'fa-swap-opacity': swapOpacity,
        },
        className
      )}
      style={{
        fontSize,
        ...(style ?? {}),
        ...(color ? { color } : {}),
      }}
      {...rest}
    />
  );
};

export default FontAwesomeCustomIcon;
